import React from 'react';
import { Accordion, Message, Button } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import Waiting from '../../components/Waiting';
import ValidationModal from '../ValidationModal';
import css from '../../styles.module.css';
import RateAttendees from './RateAttendees';
import { useSegment } from '../../../../../components/tracking/trackingProvider';
import { getInventoryName } from '../utils';
import useAppContext from '../../../../../context/useAppContext';

function RateManagerFormAttendee({
    form,
    onChangeAttendee = () => {},
    onChangeFormAttendees = () => {},
    inventories = [],
    categories,
    setFullScreenMode,
    setSelectedZone,
    setPreviousZone,
    setBackToSeats,
    onChangeSteps,
    bookingSelectionAuto
}) {
    const [{ event }] = useAppContext();

    if (form.use_sessions) {
        return form.sessions.map(session => (
            <Accordion key={session.id} disableToggle={true} openByDefault title={session.text}>
                {form.selected_rates
                    .filter(selected_rate => selected_rate.rate.sessionId === session.value)
                    .map(rate => (
                        <RateAttendees
                            key={rate.id}
                            onChangeAttendee={onChangeAttendee}
                            onChangeFormAttendees={onChangeFormAttendees}
                            form={form}
                            selected_rate={rate}
                            attendees={form.attendees.filter(attendee => attendee.rate_id === rate.rate.id)}
                        />
                    ))}
            </Accordion>
        ));
    }

    if (form.use_inventories) {
        let inventories_ids = [];
        form.attendees.forEach(attendee => {
            if (attendee.booking_uuid && !inventories_ids.includes(attendee.booking_uuid.inventory)) {
                inventories_ids.push(attendee.booking_uuid.inventory);
            }
        });

        let inventories_obj = inventories.filter(inventory => inventories_ids.includes(inventory.pk));

        return inventories_obj.map(inventory => {
            // Get for each inventory the attendees
            let attendees = form.attendees.filter(attendee => attendee?.booking_uuid.inventory === inventory.pk);

            // get rates_ids to map only rates that we need
            let rates_ids = [];
            attendees.forEach(attendee => {
                if (!rates_ids.includes(attendee.rate_id)) {
                    rates_ids.push(attendee.rate_id);
                }
            });

            return (
                <Accordion key={inventory.pk} disableToggle={true} openByDefault title={getInventoryName(event.isFakeSeating(), inventory, event.timezone)}>
                    {form.selected_rates
                        .filter(s_rate => rates_ids.includes(s_rate.rate.id))
                        .map(selected_rate => (
                            <RateAttendees
                                key={selected_rate.rate.id}
                                form={form}
                                categories={categories}
                                bookingSelectionAuto={bookingSelectionAuto}
                                selected_rate={selected_rate}
                                attendees={attendees.filter(attendee => attendee.rate_id === selected_rate.rate.id)}
                                onChangeAttendee={onChangeAttendee}
                                onChangeFormAttendees={onChangeFormAttendees}
                                setFullScreenMode={setFullScreenMode}
                                setSelectedZone={setSelectedZone}
                                setPreviousZone={setPreviousZone}
                                setBackToSeats={setBackToSeats}
                                onChangeSteps={onChangeSteps}
                            />
                        ))}
                </Accordion>
            );
        });
    }

    return form.selected_rates.map(selected_rate => {
        return (
            <RateAttendees
                key={selected_rate.rate.id}
                form={form}
                selected_rate={selected_rate}
                attendees={form.attendees.filter(attendee => attendee.rate_id === selected_rate.rate.id)}
                onChangeAttendee={onChangeAttendee}
                onChangeFormAttendees={onChangeFormAttendees}
            />
        );
    });
}

export default function AttendeesStep({
    form,
    onChangeForm = () => {},
    handleSubmit = () => {},
    isSubmitting,
    inventories = [],
    step,
    categories,
    onChangeSteps = () => {},
    setFullScreenMode,
    setSelectedZone,
    setPreviousZone,
    setBackToSeats,
    bookingSelectionAuto
}) {
    const { t } = useTranslation();
    const { track } = useSegment();

    const handleContinue = React.useCallback(() => {
        onChangeSteps(step + 1);
        track('ACTION > VALIDATE ATTENDEES INFORMATION', { 'Page Title': 'ADD ATTENDEES' });
    }, [onChangeSteps, step]);

    const handleModal = React.useCallback(() => {
        onChangeForm('validationModal', true);
        track('ACTION > ADD NON-NOMINATIVE ATTENDEES', { 'Page Title': 'ADD ATTENDEES', Steps: 'Attendees' });
    }, [onChangeForm]);

    const onChangeAttendee = React.useCallback(
        (index, key, value) => {
            let attendees = [...form.attendees];
            let attendee = attendees.find(a => a.id === index);
            attendee.form[key] = value;
            onChangeForm('attendees', attendees);
        },
        [onChangeForm, form]
    );

    const onChangeFormAttendees = React.useCallback(
        (index, value) => {
            let attendees = [...form.attendees];
            let attendee = attendees.find(a => a.id === index);
            attendee.form = value;
            onChangeForm('attendees', attendees);
        },
        [onChangeForm, form]
    );

    return (
        <Waiting for={form.attendees.length}>
            <Message type="default" title={t('attendees.addAttendees.addBuyerForm.message-title')}>
                {t('attendees.addAttendees.addBuyerForm.message-subtitle')}
            </Message>
            <RateManagerFormAttendee
                form={form}
                onChangeAttendee={onChangeAttendee}
                onChangeFormAttendees={onChangeFormAttendees}
                categories={categories}
                inventories={inventories}
                onChangeSteps={onChangeSteps}
                setFullScreenMode={setFullScreenMode}
                setSelectedZone={setSelectedZone}
                setPreviousZone={setPreviousZone}
                setBackToSeats={setBackToSeats}
                bookingSelectionAuto={bookingSelectionAuto}
            />
            <div className={css['select-labels-btns']}>
                <Button className={css['progress-buttons']} primary disabled={!form.selected_rates.length} label={t('ticket.common.action.continue')} onClick={handleContinue} />
                <Button
                    disabled={!form.selected_rates.length}
                    label={t('attendees.addAttendees.addNonNominativeAttendees')}
                    onClick={handleModal}
                    className={css['tweak-label-btn']}
                />
            </div>
            {form.validationModal && <ValidationModal form={form} setForm={onChangeForm} handleSubmit={handleSubmit} isSubmitting={isSubmitting} />}
        </Waiting>
    );
}
